// Fetches the admin-managed releases JSON directly from Azure Blob Storage
// (anonymous blob-level read). No auth header; CORS is open since the blob
// container is public. Set VITE_RELEASES_BLOB_URL at build/deploy time.

export type ReleaseChannel = 'stable' | 'experimental';

export interface ReleaseEntry {
  version: string;
  channel: ReleaseChannel;
  addedAt?: string;
  addedBy?: string;
}

export interface ReleasesDoc {
  updatedAt: string | null;
  releases: ReleaseEntry[];
}

const DEFAULT_BLOB_URL =
  'https://loxiaautopilotweb.blob.core.windows.net/public-releases/releases.json';

export function getReleasesBlobUrl(): string {
  return (import.meta.env.VITE_RELEASES_BLOB_URL as string | undefined) || DEFAULT_BLOB_URL;
}

/**
 * Fetch the current releases list. Returns an empty document on any failure
 * so callers can render an empty state instead of crashing.
 */
export async function fetchReleases(): Promise<ReleasesDoc> {
  try {
    const res = await fetch(getReleasesBlobUrl(), { cache: 'default' });
    if (!res.ok) {
      console.warn(`[downloads] fetchReleases HTTP ${res.status}`);
      return { updatedAt: null, releases: [] };
    }
    const data = await res.json();
    if (!data || !Array.isArray(data.releases)) {
      return { updatedAt: null, releases: [] };
    }
    return data as ReleasesDoc;
  } catch (err) {
    console.warn('[downloads] fetchReleases failed', err);
    return { updatedAt: null, releases: [] };
  }
}

/**
 * Pick the newest release per channel from a doc whose `releases` array is
 * already sorted newest-first.
 */
export function splitByChannel(doc: ReleasesDoc): {
  stable: ReleaseEntry | null;
  experimental: ReleaseEntry | null;
} {
  const stable = doc.releases.find(r => r.channel === 'stable') || null;
  const experimental = doc.releases.find(r => r.channel === 'experimental') || null;
  return { stable, experimental };
}
